/**
 * useDancefloorTheme Hook
 *
 * Resolves and returns the appropriate gesture theme for a given user.
 * The theme determines the space name, gesture labels, and descriptors.
 *
 * Usage:
 * - Pass a User object if you already have it (most efficient)
 * - Pass a userId if you need to fetch the user
 * - Pass nothing to use the current user's theme
 */

import { useState, useEffect, useMemo } from 'react';
import { User } from 'shared/types/platformTypes';
import { GestureTheme, getGestureTheme } from 'shared/constants/gestureThemes';
import { getUserFromAPI } from '../api/api';
import { useAuth } from './useAuth';

interface UseDancefloorThemeOptions {
  user?: User | null;
  userId?: string;
}

/**
 * Hook to get the gesture theme for a user
 *
 * @param options.user - User object if already available
 * @param options.userId - User ID to fetch if user object not provided
 * @returns The resolved gesture theme
 */
export const useDancefloorTheme = (options?: UseDancefloorThemeOptions): GestureTheme => {
  const { userProfile } = useAuth();
  const [fetchedUser, setFetchedUser] = useState<User | null>(null);

  const { user: providedUser, userId } = options || {};

  // Fetch user if userId provided but user object not provided
  useEffect(() => {
    if (userId && !providedUser && userId !== userProfile?.userId) {
      getUserFromAPI(userId)
        .then(setFetchedUser)
        .catch(err => {
          console.error('Error fetching user for theme:', err);
          setFetchedUser(null);
        });
    } else if (!userId || userId === userProfile?.userId) {
      // Clear fetched user if we're using current user or no user specified
      setFetchedUser(null);
    }
  }, [userId, providedUser, userProfile?.userId]);

  // Determine which user object to use
  const targetUser = useMemo(() => {
    if (providedUser) return providedUser;
    if (userId === userProfile?.userId) return userProfile;
    return fetchedUser;
  }, [providedUser, userId, userProfile, fetchedUser]);

  // Get theme from user settings
  const theme = useMemo(() => {
    const themeKey = targetUser?.settings?.gestureTheme;
    return getGestureTheme(themeKey);
  }, [targetUser]);

  return theme;
};

/**
 * Simplified hook for when you just need the current user's theme
 */
export const useCurrentUserTheme = (): GestureTheme => {
  const { userProfile } = useAuth();

  return useMemo(() => {
    const themeKey = userProfile?.settings?.gestureTheme;
    return getGestureTheme(themeKey);
  }, [userProfile]);
};
